import * as DialogPrimitive from '@radix-ui/react-dialog';

import { Button } from '@/components/ui/button';

interface SealRitualDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What's being sealed — drives the dialog copy ("note" or "case report"). */
  kind: 'note' | 'report';
  /** Confirm action — seals the document. Should already have flushed any
   *  pending edits before being called. */
  onConfirm: () => void;
  isPending?: boolean;
}

/**
 * The ritual that turns a draft into a sealed clinical record.
 *
 * Deliberately quieter than a generic confirm dialog — no red destructive
 * styling, no exclamation. The act of sealing a clinical note is a
 * deliberate one but it's also a normal part of the practitioner's day,
 * and the chrome should match that. Mint accent strip on the leading edge
 * mirrors the journal page; the wax-seal motif sits inside the dialog.
 */
export function SealRitualDialog({
  open,
  onOpenChange,
  kind,
  onConfirm,
  isPending = false,
}: SealRitualDialogProps) {
  const copy = kind === 'note' ? NOTE_COPY : REPORT_COPY;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-(--color-accent-ink)/30 backdrop-blur-[2px]" />
        <DialogPrimitive.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 bg-surface fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl shadow-[0_24px_60px_rgba(3,68,92,0.22)]">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-(--color-primary-300) to-transparent"
          />
          <div className="flex flex-col gap-6 px-7 pt-7 pb-6">
            <div className="flex items-start gap-4">
              <WaxSealMark />
              <div className="flex flex-col gap-2">
                <DialogPrimitive.Title className="font-display text-title-md text-accent-ink leading-snug italic">
                  {copy.title}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-body-sm leading-relaxed text-neutral-600">
                  {copy.description}
                </DialogPrimitive.Description>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={isPending}
              >
                Keep editing
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={onConfirm}
                disabled={isPending}
                loading={isPending}
              >
                {copy.confirmLabel}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const NOTE_COPY = {
  title: 'Seal this note',
  description:
    'A sealed note becomes part of the case record and can’t be edited again. Take a moment to read it through.',
  confirmLabel: 'Seal note',
};

const REPORT_COPY = {
  title: 'Submit for review',
  description:
    'The chief psychologist will be notified. You can still edit if it comes back rejected — submitting is not final.',
  confirmLabel: 'Submit for review',
};

/** The wax-seal motif used in the dialog header — small circular Roobert SemiMono "S"
 *  on a mint disc with a thin gold rim. Matches the SealedMark element shown
 *  in the corner of a sealed note's header. */
function WaxSealMark() {
  return (
    <span
      aria-hidden="true"
      className="relative flex h-12 w-12 flex-none items-center justify-center rounded-full bg-(--color-mint-paper)"
    >
      <span className="absolute inset-0 rounded-full ring-1 ring-(--color-numeral-gold)/40" />
      <span className="font-display text-[24px] leading-none text-(--color-primary-700) italic">
        S
      </span>
    </span>
  );
}
